import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFetchWithSpinner } from "../../hooks/useFetchWithSpinner";
import {
  BarChartSummary,
  BarChartComparison,
  VerticalPositiveNegativeBarChart,
  VerticalBarChartComparison,
  ComparativeTable,
  LineChartComparison,
  MultiCustomPieChart
} from "../Charts";
import { selectDates, selectTopN, selectComparisonYear } from "../../redux/slices/filtersSlice";
import {
  filterData,
  processFilteredData,
  groupAgeByRange,
  applyFilterLogic
} from "../../utils/filterFunctions";
import "./Pages.css";

const LesionesPersonales = () => {
  const [data, setData] = useState([]);

  const dates = useSelector(selectDates);
  const comparisonYear = useSelector(selectComparisonYear);
  const topN = useSelector(selectTopN);

  const baseFilters = {
    modalidad: null,
    tipo_arma: null,
    sexo: null,
    rangoEdad: null,
    diaJornada: null,
    mes: null,
    comuna: null,
  };
  const [filters, setFilters] = useState({ ...baseFilters });
  const [frozenKey, setFrozenKey] = useState(null);
  const fetchWithSpinner = useFetchWithSpinner();

  useEffect(() => {
    if (dates.start && dates.end) {
      fetchWithSpinner({
        startDate: dates.start,
        endDate: dates.end,
        year_to_compare: comparisonYear,
        endpoint: 'lesionesPersonales/',
      }, setData);
    }
  }, [dates.start, dates.end, comparisonYear]);

  const currentYear = dates.end ? new Date(dates.end).getFullYear() : new Date().getFullYear();

  const filteredData = filterData(data, filters);

  const dias = [...new Set(data.map(h => h.dia))];
  const jornadas = [...new Set(data.map(h => h.jornada))];

  const results = processFilteredData(filteredData, {
    includeTotals: true,
    groupByFields: ["modalidad", "tipo_arma", "mes", "sexo", "comuna"],
    includeDayShift: true,
    dias,
    jornadas
  }, comparisonYear, currentYear);

  const { totals: { total1, total2, diferencia, variacion }, resumenData } = results;

  const modalidadDataRaw = results.groups.modalidad.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const modalidadData = modalidadDataRaw.slice(0, Math.min(topN, modalidadDataRaw.length));

  const tipoArmaDataRaw = results.groups.tipo_arma.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const tipoArmaData = tipoArmaDataRaw.slice(0, Math.min(topN, tipoArmaDataRaw.length));

  const mesData = results.groups.mes.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

  const sexoData = results.groups.sexo
    .filter(item => item.name === "Hombre" || item.name === "Mujer")
    .sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

  const comunaData = results.groups.comuna.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

  const diasJornadaData1 = results.diasJornadaData[comparisonYear];
  const diasJornadaData2 = results.diasJornadaData[currentYear];

  const ranges = [
    { label: "Blank", min: null, max: null },
    { label: "0-5", min: 0, max: 5 },
    { label: "6-11", min: 6, max: 11 },
    { label: "12-13", min: 12, max: 13 },
    { label: "14-17", min: 14, max: 17 },
    { label: "18-24", min: 18, max: 24 },
    { label: "25-28", min: 25, max: 28 },
    { label: "29-32", min: 29, max: 32 },
    { label: "33-38", min: 33, max: 38 },
    { label: "39-45", min: 39, max: 45 },
    { label: "46-52", min: 46, max: 52 },
    { label: "53-59", min: 53, max: 59 },
    { label: "60-66", min: 60, max: 66 },
    { label: "Mayor-67", min: 67, max: Infinity },
    { label: "Sin Dato", min: null, max: null },
  ];
  const edadData = groupAgeByRange(filteredData, ranges, comparisonYear, currentYear).sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

  const handleFilterClick = (tipo, valor, source) => {
    setFilters((prevFilters) =>
      applyFilterLogic(prevFilters, tipo, valor, source, frozenKey, setFrozenKey, baseFilters)
    );
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-grid">
        <div className="row top">
          <div className="card small">
            <BarChartSummary
              resumenData={resumenData}
              diferencia={diferencia}
              variacion={variacion}
              xKey="name"
              title="Resumen general"
              selectedKey={filters.año}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("año", d?.category, "Resumen general")}
            />
          </div>
          <div className="card small">
            <VerticalBarChartComparison
              showData={modalidadData}
              xKey="name"
              title="Lesiones por modalidad"
              selectedKey={filters.modalidad}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("modalidad", d?.category, "Lesiones por modalidad")}
            />
          </div>
          <div className="card small">
            <VerticalBarChartComparison
              showData={tipoArmaData}
              xKey="name"
              title="Lesiones por tipo de arma"
              selectedKey={filters.tipo_arma}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("tipo_arma", d?.category, "Lesiones por tipo de arma")} />
          </div>
          <div className="card wide">
            <MultiCustomPieChart
              showData={sexoData}
              xKey="name"
              title="Lesiones por sexo"
              selectedKey={filters.sexo}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("sexo", d?.category, "Lesiones por sexo")}
            />
          </div>
        </div>
        <div className="row middle">
          <div className="column-right">
            <div className="row bottom">
              <div className="card medium">
                <VerticalPositiveNegativeBarChart
                  showData={edadData}
                  xKey="rango"
                  title="Rango de edad"
                  selectedKey={filters.rangoEdad}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("rangoEdad", d?.category, "Rango de edad")}
                  comparisonYear={comparisonYear}
                />
              </div>
              <div className="card medium">
                <LineChartComparison
                  showData={mesData}
                  xKey="name"
                  title="Lesiones por mes"
                  selectedKey={filters.mes}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("mes", d?.category, "Lesiones por mes")}
                />
              </div>
            </div>
            <div className="row bottom">
              <div className="card medium">
                <BarChartComparison
                  showData={comunaData}
                  xKey="name"
                  title="Lesiones por comuna"
                  selectedKey={filters.comuna}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("comuna", d?.category, "Lesiones por comuna")}
                />
              </div>
              <div className="card medium">
                <ComparativeTable
                  previousData={diasJornadaData1}
                  previousYear={comparisonYear}
                  currentData={diasJornadaData2}
                  currentYear={currentYear}
                  title="Lesiones por día y jornada"
                  selectedKey={filters.diaJornada}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("diaJornada", d?.category, "Lesiones por día y jornada")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LesionesPersonales;
